/**
 * Schema-driven intelligence for the Rego policy editor.
 *
 * The OPA `input` contract is defined ONCE, server-side, as a JSON Schema
 * (packages/worker/src/policy/schema/policyInput.schema.json) and served from the public
 * GET /api/policy-schema/:artefact endpoint. Everything in this module is a pure function
 * over that fetched document, so completions, hovers and the (?) helper all read the same
 * source of truth and can never drift from what the scanners actually hand to OPA.
 */

/** The JSON-Schema (draft-07) subset the policy-input contract uses. */
export interface SchemaNode {
  type?: string | string[]
  description?: string
  title?: string
  properties?: Record<string, SchemaNode>
  required?: string[]
  items?: SchemaNode
  enum?: unknown[]
  const?: unknown
  $ref?: string
}

export interface SchemaDoc extends SchemaNode {
  $defs?: Record<string, SchemaNode>
}

type Defs = Record<string, SchemaNode> | undefined

/** `#/$defs/Summary` -> `Summary`. */
export function refName(ref: string): string {
  return ref.replace('#/$defs/', '')
}

export function resolveRef(ref: string, defs: Defs): SchemaNode | null {
  return defs?.[refName(ref)] ?? null
}

/** Follow `$ref` until a concrete node (bounded, so a cyclic schema cannot hang the editor). */
export function resolveNode(node: SchemaNode, defs: Defs): SchemaNode {
  let current = node
  for (let hop = 0; hop < 8 && current.$ref; hop++) {
    const next = resolveRef(current.$ref, defs)
    if (!next) return current
    current = next
  }
  return current
}

/** Human-readable type for a field row: `integer`, `string | null`, `FindingPrimitive[]`, `"module"`. */
export function typeLabel(node: SchemaNode, defs?: Defs): string {
  if (node.$ref) return refName(node.$ref)
  if (node.const !== undefined) return `"${String(node.const)}"`
  if (node.enum) return node.enum.filter((e) => e !== null).map((e) => `"${String(e)}"`).join(' | ')
  const t = Array.isArray(node.type) ? node.type.filter((x) => x !== 'null').join(' | ') : node.type
  if (t === 'array' && node.items) return `${typeLabel(node.items, defs)}[]`
  return t ?? 'any'
}

function isArrayNode(node: SchemaNode): boolean {
  return Array.isArray(node.type) ? node.type.includes('array') : node.type === 'array'
}

// ── Path walking ──────────────────────────────────────────────────────────────

type PathSegment = { kind: 'prop'; name: string } | { kind: 'index' }

const PROP_SEG  = /^\.([A-Za-z_]\w*)/
const INDEX_SEG = /^\[[^\]]*\]/

/** Split `input.findings[_].meta` into its segments. Null when the expression is not rooted at `input`. */
export function parsePath(expr: string): PathSegment[] | null {
  if (!/^input(?![\w])/.test(expr)) return null
  let rest = expr.slice('input'.length)
  const segments: PathSegment[] = []
  while (rest.length > 0) {
    const prop = PROP_SEG.exec(rest)
    if (prop) {
      segments.push({ kind: 'prop', name: prop[1]! })
      rest = rest.slice(prop[0].length)
      continue
    }
    const index = INDEX_SEG.exec(rest)
    if (index) {
      segments.push({ kind: 'index' })
      rest = rest.slice(index[0].length)
      continue
    }
    return null
  }
  return segments
}

/**
 * Resolve an `input.*` expression to its schema node, keeping both the node as written
 * (a `$ref`, carrying the property's own description) and the node it resolves to.
 *
 * Array fields must be indexed (`input.findings[_]`) exactly as Rego requires - a bare
 * `input.findings.severity` resolves to nothing rather than completing a reference that
 * would never match at evaluation time.
 */
export function walkPath(doc: SchemaDoc, expr: string): { raw: SchemaNode; resolved: SchemaNode } | null {
  const segments = parsePath(expr)
  if (!segments) return null
  let raw: SchemaNode = doc
  let resolved: SchemaNode = resolveNode(doc, doc.$defs)
  for (const segment of segments) {
    if (segment.kind === 'index') {
      if (!isArrayNode(resolved) || !resolved.items) return null
      raw = resolved.items
    } else {
      const child = resolved.properties?.[segment.name]
      if (!child) return null
      raw = child
    }
    resolved = resolveNode(raw, doc.$defs)
  }
  return { raw, resolved }
}

/** The resolved schema node an `input.*` expression points at, or null if it points nowhere. */
export function nodeAtPath(doc: SchemaDoc, expr: string): SchemaNode | null {
  return walkPath(doc, expr)?.resolved ?? null
}

// ── Field suggestions ─────────────────────────────────────────────────────────

export interface FieldSuggestion {
  /** What the user sees, e.g. `findings` or the indexed `findings[_]` variant. */
  label: string
  /** What gets typed into the buffer (differs from the label for the indexed variant). */
  insertText: string
  /** Rendered type, used as the suggestion's detail line. */
  type: string
  description?: string
  required: boolean
  /** Full dotted path, shown in hover/documentation so the reader keeps their bearings. */
  path: string
}

/**
 * The fields available directly under `expr`. For an array field we also offer the indexed
 * form, which is the only one you can keep dotting through.
 */
export function fieldSuggestions(doc: SchemaDoc, expr: string): FieldSuggestion[] {
  const parent = nodeAtPath(doc, expr)
  const properties = parent?.properties
  if (!properties) return []
  const required = new Set(parent.required ?? [])

  const out: FieldSuggestion[] = []
  for (const [name, raw] of Object.entries(properties)) {
    const resolved = resolveNode(raw, doc.$defs)
    // A description on the property itself is more specific than the shared $def's.
    const description = raw.description ?? resolved.description
    out.push({
      label: name,
      insertText: name,
      type: typeLabel(raw, doc.$defs),
      required: required.has(name),
      path: `${expr}.${name}`,
      ...(description ? { description } : {}),
    })
    if (isArrayNode(resolved) && resolved.items) {
      const item = resolveNode(resolved.items, doc.$defs)
      out.push({
        label: `${name}[_]`,
        insertText: `${name}[_]`,
        type: typeLabel(resolved.items, doc.$defs),
        required: required.has(name),
        path: `${expr}.${name}[_]`,
        ...(item.description ? { description: `One element of \`${name}\`. ${item.description}` } : {}),
      })
    }
  }
  return out
}

// ── Reading the expression at the cursor ──────────────────────────────────────

/** Identifier (or `input`) followed by any number of `.prop` / `[...]` segments. */
const CHAIN_SOURCE = '[A-Za-z_]\\w*(?:\\.[A-Za-z_]\\w*|\\[[^\\]\\s]*\\])*'
const CHAIN_BEFORE_DOT = new RegExp(`(?:^|[^A-Za-z0-9_.\\]])(${CHAIN_SOURCE})\\.([A-Za-z_]\\w*)?$`)

/**
 * Local bindings onto `input`, so `f := input.findings[_]` then `f.` completes finding fields.
 * Both Rego idioms are recognised: `x := <input path>` and `some x in <input path>`.
 */
export function aliasMap(source: string): Record<string, string> {
  const aliases: Record<string, string> = {}
  const assign = new RegExp(`([A-Za-z_]\\w*)\\s*:=\\s*(input(?:\\.[A-Za-z_]\\w*|\\[[^\\]\\s]*\\])*)`, 'g')
  for (const m of source.matchAll(assign)) aliases[m[1]!] = m[2]!
  const iterate = new RegExp(`\\bsome\\s+([A-Za-z_]\\w*)\\s+in\\s+(input(?:\\.[A-Za-z_]\\w*|\\[[^\\]\\s]*\\])*)`, 'g')
  // `some x in <array>` binds x to an ELEMENT, so the alias carries the index step.
  for (const m of source.matchAll(iterate)) aliases[m[1]!] = `${m[2]!}[_]`
  return aliases
}

/** Rewrite an alias-rooted chain (`f.category`) onto `input.…`; null when the root is unknown. */
export function resolveChain(chain: string, aliases: Record<string, string> = {}): string | null {
  if (/^input(?![\w])/.test(chain)) return chain
  const root = /^[A-Za-z_]\w*/.exec(chain)
  if (!root) return null
  const target = aliases[root[0]]
  if (!target) return null
  return `${target}${chain.slice(root[0].length)}`
}

/**
 * What the cursor is completing: the `input.*` path whose members should be offered, plus the
 * partial word already typed. Null when the cursor is not after a member access we can resolve.
 */
export function completionTarget(
  lineUpToCursor: string,
  aliases: Record<string, string> = {},
): { path: string; partial: string } | null {
  const m = CHAIN_BEFORE_DOT.exec(lineUpToCursor)
  if (!m) return null
  const path = resolveChain(m[1]!, aliases)
  if (!path) return null
  return { path, partial: m[2] ?? '' }
}

/** The whole member chain under a 1-based column, for hover. */
export function chainAt(line: string, column: number): string | null {
  const cursor = Math.max(0, Math.min(line.length, column - 1))
  const isChainChar = (ch: string) => /[A-Za-z0-9_.[\]]/.test(ch)
  let start = cursor
  while (start > 0 && isChainChar(line[start - 1]!)) start--
  let end = cursor
  while (end < line.length && isChainChar(line[end]!)) end++
  const token = line.slice(start, end).replace(/\.+$/, '')
  return token.length > 0 ? token : null
}

/** Hover copy for an `input.*` path: `path`, type, and the contract's own description. */
export function describePath(doc: SchemaDoc, path: string): { path: string; type: string; description?: string } | null {
  const hit = walkPath(doc, path)
  if (!hit) return null
  const description = hit.raw.description ?? hit.resolved.description
  return {
    path,
    type: typeLabel(hit.raw, doc.$defs),
    ...(description ? { description } : {}),
  }
}
